import { create } from 'zustand'
import apiHelper from '@/network/api'

interface ChartValue {
  keyword: string
  count: number
}

interface ChartDataPoint {
  date: string
  values: ChartValue[]
}

interface Post {
  id: string
  relatedKeywordId: string
  postId: string
  permalink: string
  code: string
  postCreatedAt: string
  username: string
  keywordText: string
  caption: string
  mediaProductType: string
  mediaType: string
  mediaUrl: string | null
  isQuotePost: boolean
  isReply: boolean
  repliedToId: string | null
  rootPostId: string | null
  createdAt: string
  updatedAt: string
  source: string
  containsKeyword: boolean
  language: string
  formattedDate: string
  replyToPost: any | null
}

interface Pagination {
  total: number
  page: number
  limit: number
  totalPages: number
}

interface KeywordDashboardState {
  chart: ChartDataPoint[]
  posts: Post[]
  pagination: Pagination | null
  page: number
  hasMore: boolean
  keywords: string[]
  isLoading: boolean
  isPaginationLoading: boolean
  error: string | null
  fetchDashboard: (keywordGroups: string[], daysBack: number, page?: number) => Promise<void>
  setChart: (chart: ChartDataPoint[]) => void
}

const useKeywordDashboardStore = create<KeywordDashboardState>((set, get) => ({
  chart: [],
  posts: [],
  pagination: null,
  page: 1,
  hasMore: true,
  keywords: [],
  isLoading: false,
  isPaginationLoading: false,
  error: null,

  fetchDashboard: async (keywords: string[], daysBack: number, page: number = 1) => {
    try {
      const isFirstPage = page === 1
      set({ 
        isLoading: isFirstPage,
        isPaginationLoading: !isFirstPage,
        error: null 
      })

      const response = await apiHelper(null).post('keyword/dashboard', {
        keywords,
        daysBack,
        page
      })
      
      if (response.status === 200) {
        const { chart, posts } = response.data
        const items = posts?.items ?? []
        if (isFirstPage) {
          set({ 
            chart,
            posts: items,
            pagination: posts.pagination,
            page,
            hasMore: items.length > 0,
            keywords,
            isLoading: false,
            isPaginationLoading: false
          })
        } else {
          set(state => ({ 
            posts: [...state.posts, ...items],
            pagination: posts.pagination,
            page,
            hasMore: items.length > 0,
            isPaginationLoading: false
          }))
        }
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch dashboard data',
        isLoading: false,
        isPaginationLoading: false
      })
      console.error('Error fetching dashboard data:', error)
    }
  },

  setChart: (chart: ChartDataPoint[]) => set({ chart })
}))

export default useKeywordDashboardStore 